import { step } from './character';
import type { CollisionWorld } from './collision';
import {
  AIR_CONTROL,
  BACKPEDAL_FACTOR,
  DT,
  JUMP_VELOCITY,
  RUN_SPEED,
} from './constants';
import {
  add,
  forwardFromYaw,
  horizontal,
  length,
  normalizeOrZero,
  rightFromYaw,
  scale,
  sub,
  v3,
  yawFromDirection,
  type V3,
} from './vec';

export interface MoveState {
  pos: V3;
  vel: V3;
  grounded: boolean;
  groundNormal: V3;
  yaw: number;
}

/** One tick of player intent. The client predicts with it and the server replays it. */
export interface MoveInput {
  seq: number;
  /** -1 back, 1 forward. */
  forward: number;
  /** -1 left, 1 right. */
  strafe: number;
  yaw: number;
  jump: boolean;
}

/** What crowd control and abilities impose on movement. The server sends it with each snapshot. */
export interface MoveControl {
  /** Multiplier on run speed from snares and speed buffs. */
  speed: number;
  rooted: boolean;
  /** No moving or turning: stuns, incapacitates, Ice Block. */
  stunned: boolean;
  /** Feared units run this way regardless of input. */
  fearDir: V3 | null;
  /** Charging units run at `speed` toward `target` regardless of input. */
  dash: { target: V3; speed: number } | null;
}

export const FREE_CONTROL: MoveControl = {
  speed: 1,
  rooted: false,
  stunned: false,
  fearDir: null,
  dash: null,
};

export const IDLE_INPUT: MoveInput = {
  seq: 0,
  forward: 0,
  strafe: 0,
  yaw: 0,
  jump: false,
};

/** Below this, a kill plane: the unit fell out of the arena. */
export const KILL_Y = -30;

/**
 * Advances one unit by one tick. WoW rules: grounded units take their input velocity outright,
 * airborne ones keep the momentum they jumped with (or drift slightly toward input if they had
 * none), and turning is always free.
 */
export function moveStep(
  world: CollisionWorld,
  state: MoveState,
  input: MoveInput,
  control: MoveControl,
): MoveState {
  let yaw = state.yaw;
  if (control.dash) {
    const to = horizontal(sub(control.dash.target, state.pos));
    if (length(to) > 1e-3) yaw = yawFromDirection(to);
  } else if (control.fearDir) {
    yaw = yawFromDirection(control.fearDir);
  } else if (!control.stunned) {
    yaw = input.yaw;
  }

  let vel = state.vel;
  let grounded = state.grounded;
  const wish = wishVelocity(state.pos, yaw, input, control);
  if (control.dash) {
    // A charge runs along the ground: it can fall off a ledge but never carries a jump upward.
    vel = v3(wish.x, Math.min(vel.y, 0), wish.z);
  } else if (grounded) {
    vel = v3(wish.x, vel.y, wish.z);
    const free = !control.rooted && !control.stunned && !control.fearDir;
    if (input.jump && free) {
      vel = v3(vel.x, JUMP_VELOCITY, vel.z);
      grounded = false;
    }
  } else if (
    length(wish) > 0 &&
    horizontalSpeed(vel) <= RUN_SPEED * control.speed * AIR_CONTROL + 1e-6
  ) {
    const drift = scale(wish, AIR_CONTROL);
    vel = v3(drift.x, vel.y, drift.z);
  }

  const result = step(
    world,
    {
      position: state.pos,
      velocity: vel,
      grounded,
      groundNormal: state.groundNormal,
    },
    DT,
  );
  return {
    pos: result.position,
    vel: result.velocity,
    grounded: result.ground !== null,
    groundNormal: result.ground?.normal ?? v3(0, 1, 0),
    yaw,
  };
}

function wishVelocity(
  pos: V3,
  yaw: number,
  input: MoveInput,
  control: MoveControl,
): V3 {
  if (control.dash) {
    const to = horizontal(sub(control.dash.target, pos));
    const dist = length(to);
    const speed = Math.min(control.dash.speed, dist / DT);
    return scale(normalizeOrZero(to), speed);
  }
  if (control.stunned || control.rooted) return v3();
  const speed = RUN_SPEED * control.speed;
  if (control.fearDir) return scale(normalizeOrZero(control.fearDir), speed);

  const forward = clampAxis(input.forward);
  const strafe = clampAxis(input.strafe);
  const direction = normalizeOrZero(
    add(scale(forwardFromYaw(yaw), forward), scale(rightFromYaw(yaw), strafe)),
  );
  return scale(direction, forward < 0 ? speed * BACKPEDAL_FACTOR : speed);
}

const clampAxis = (value: number): number =>
  Number.isFinite(value) ? Math.max(-1, Math.min(1, Math.round(value))) : 0;

/** Horizontal speed a unit is moving at, for cast cancelling and animation. */
export const horizontalSpeed = (vel: V3): number => length(horizontal(vel));
